/**
 * Error taxonomy shared by the kernel components.
 *
 * `LimitError` covers the "documented limit exceeded" class: an 11th sheet, a
 * row past 5,000,000, a column past `XFD`, an oversized tile request. Every
 * limit check runs *before* any mutation, so a rejected operation can never
 * leave partial state (Requirement 16.1, Properties 2, 3, 64).
 *
 * The message always carries both the limit and the requested value, because
 * Requirement 16.1 asks the UI to display exactly those two numbers.
 */

/** Unit a limit is expressed in, used verbatim in the message. */
export type LimitUnit = 'rows' | 'columns' | 'sheets' | 'cells' | 'bytes';

/** A rejected action that exceeded a documented Sheet, Workbook, or file limit. */
export class LimitError extends Error {
  override readonly name = 'LimitError';
  /** The documented limit that was exceeded. */
  readonly limit: number;
  /** The value the caller asked for. */
  readonly requested: number;
  /** Unit both numbers are expressed in. */
  readonly unit: LimitUnit;

  constructor(limit: number, requested: number, unit: LimitUnit, what = 'limit exceeded') {
    super(`${what}: limit ${limit} ${unit}, requested ${requested} ${unit}`);
    this.limit = limit;
    this.requested = requested;
    this.unit = unit;
  }
}

/** Type guard, so callers can branch without `instanceof` across worker boundaries. */
export function isLimitError(e: unknown): e is LimitError {
  return e instanceof LimitError;
}

/** A command addressed a sheet id the Workbook does not hold. */
export class UnknownSheetError extends Error {
  override readonly name = 'UnknownSheetError';
  /** The sheet id that could not be resolved. */
  readonly sheet: string;

  constructor(sheet: string) {
    super(`unknown sheet: ${JSON.stringify(sheet)}`);
    this.sheet = sheet;
  }
}

/**
 * A rejected sheet name: blank, over-long, containing `!` (which would break
 * cross-sheet Cell_References), or already used by another sheet.
 */
export class SheetNameError extends Error {
  override readonly name = 'SheetNameError';
  readonly requested: string;
  readonly reason: string;

  constructor(requested: string, reason: string) {
    super(`invalid sheet name ${JSON.stringify(requested)}: ${reason}`);
    this.requested = requested;
    this.reason = reason;
  }
}

/**
 * A pivot table whose target region would overwrite existing content
 * (Requirement 8.8).
 *
 * Carries the overlapping Cell_Reference as A1 text because that is precisely what
 * Requirement 8.8 asks the UI to display, and the message repeats it for the same
 * reason {@link LimitError} repeats its two numbers: the message is what a user
 * copies into a bug report. References arrive already formatted, so this module
 * stays free of the A1 conversion and of every other dependency.
 */
export class PivotPlacementError extends Error {
  override readonly name = 'PivotPlacementError';
  /** The A1 reference of the cell that stands in the way. */
  readonly cell: string;
  /** The A1 reference of the region the pivot asked for. */
  readonly footprint: string;

  constructor(cell: string, footprint: string, what = 'pivot placement overlaps existing data') {
    super(`${what}: ${cell} in ${footprint} is not empty`);
    this.cell = cell;
    this.footprint = footprint;
  }
}
